import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import PageHeader1 from '../../components/common/PageHeader1';

function BonusExpiryConfig() {
    const BASE_URL = process.env.REACT_APP_BASEURL;
    const getAuthToken = () => localStorage.getItem("astroVani-admin-token");

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            referral_bonus: { expiryDays: '', isEnabled: false },
            cashback: { expiryDays: '', isEnabled: false },
            gift: { expiryDays: '', isEnabled: false }
        }
    });

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(`${BASE_URL}/admin/bonusExpiryConfig`, {
                    headers: {
                        'Authorization': getAuthToken(),
                    },
                });

                if (!response.ok) throw new Error("Failed to fetch bonus expiry config");
                const data = await response.json();

                const configData = data?.data?.value || data?.data;

                if (configData) {
                    reset({
                        referral_bonus: {
                            expiryDays: configData.referral_bonus?.expiryDays !== null && configData.referral_bonus?.expiryDays !== undefined ? configData.referral_bonus.expiryDays : '',
                            isEnabled: configData.referral_bonus?.isEnabled || false
                        },
                        cashback: {
                            expiryDays: configData.cashback?.expiryDays !== null && configData.cashback?.expiryDays !== undefined ? configData.cashback.expiryDays : '',
                            isEnabled: configData.cashback?.isEnabled || false
                        },
                        gift: {
                            expiryDays: configData.gift?.expiryDays !== null && configData.gift?.expiryDays !== undefined ? configData.gift.expiryDays : '',
                            isEnabled: configData.gift?.isEnabled || false
                        }
                    });
                }
            } catch (error) {
                console.error("Fetch error:", error);
                toast.error(error.message || "Failed to fetch details");
            }
        };

        fetchConfig();
    }, [BASE_URL, reset]);

    const onSubmit = async (data) => {
        try {
            const formatSection = (section, sectionName) => {
                if (section.expiryDays !== null && section.expiryDays !== undefined && section.expiryDays !== '') {
                    const daysStr = String(section.expiryDays);
                    if (daysStr.includes('.') || !/^\d+$/.test(daysStr)) {
                        throw new Error(`${sectionName}: Only whole integer values are allowed for expiry days.`);
                    }
                }
                return {
                    isEnabled: Boolean(section.isEnabled),
                    expiryDays: section.expiryDays && section.expiryDays !== '' ? parseInt(section.expiryDays, 10) : null
                };
            };

            const payload = {
                referral_bonus: formatSection(data.referral_bonus, 'Referral Bonus'),
                cashback: formatSection(data.cashback, 'Cashback'),
                gift: formatSection(data.gift, 'Gift')
            };

            const response = await fetch(`${BASE_URL}/admin/bonusExpiryConfig`, {
                method: 'PATCH',
                headers: {
                    'Authorization': getAuthToken(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (!response.ok || (result && result.success === false) || (result && result.status === 'error')) {
                throw new Error(result.message || "Failed to update bonus expiry config");
            }

            toast.success(result.message || "Bonus expiry config updated successfully");
        } catch (error) {
            console.error("Update error:", error);
            toast.error(error.message || "Failed to update config");
        }
    };

    const renderConfigSection = (title, key) => (
        <div className="card mb-3">
            <div className="card-header py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom-0">
                <h6 className="m-0 fw-bold">{title}</h6>
                <div className="form-check form-switch" style={{fontSize: "16px"}}>
                    <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id={`${key}IsEnabledToggle`} 
                        {...register(`${key}.isEnabled`)} 
                    />
                    <label className="form-check-label ms-2" htmlFor={`${key}IsEnabledToggle`}>
                        Enable
                    </label>
                </div>
            </div>
            <div className="card-body pt-0">
                <div className="row g-3">
                    <div className="col-md-6">
                        <label className="form-label">Expiry Days</label>
                        <input
                            type="number"
                            step="1"
                            onKeyDown={(e) => {
                                if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                            onPaste={(e) => {
                                const pasteData = e.clipboardData.getData('text');
                                if (pasteData.includes('.') || !/^\d+$/.test(pasteData.trim())) {
                                    e.preventDefault();
                                    toast.error("Only whole integer numbers are allowed");
                                }
                            }}
                            className={`form-control ${errors[key]?.expiryDays ? 'is-invalid' : ''}`}
                            {...register(`${key}.expiryDays`, {
                                min: { value: 1, message: "Expiry days must be at least 1" },
                                max: { value: 3650, message: "Expiry days cannot exceed 3650" },
                                validate: (val) => {
                                    if (val === '' || val === null || val === undefined) return true;
                                    if (String(val).includes('.') || !/^\d+$/.test(String(val))) {
                                        return "Only integer values are allowed (no decimals)";
                                    }
                                    return true;
                                }
                            })}
                            placeholder="e.g. 30"
                        />
                        {errors[key]?.expiryDays && <span className="text-danger">{errors[key]?.expiryDays?.message || "Expiry days must be between 1 and 3650"}</span>}
                        <div className="text-muted small mt-1">Leave empty for no expiry if disabled, or specify up to 3650 days.</div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="container-xxl">
            <PageHeader1 pagetitle="Bonus Expiry Config" />
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row g-3 mb-3">
                    <div className="col-12">
                        {renderConfigSection('Referral Bonus Config', 'referral_bonus')}
                        {renderConfigSection('Cashback Config', 'cashback')}
                        {renderConfigSection('Gift Config', 'gift')}
                        <div className="card">
                            <div className="card-body">
                                <button type="submit" className="btn btn-primary">Save Config</button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default BonusExpiryConfig;
